import { RouteObject } from "react-router-dom";
import { Components, roleInitialPaths } from "./routeConfig";
import RouteErrorBoundary from "../components/common/RouteErrorBoundary";
import { CapabilityType, RoleTypes } from "../../types";
import { capabilityToRole } from "../constants/capabilities";

/**
 * Get routes configuration for a specific role
 * @param role - User role string
 * @returns Array of RouteObject for the given role
 */
export function getRoutes(role: string | null): RouteObject[] {
  if (!role) {
    return [];
  }

  switch (role) {
    case "Super_Admin":
      return [
        {
          path: "dashboard",
          element: <Components.SuperAdminDashboard />,
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: "manage-leas",
          element: <Components.ManageLEAPage />,
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: "manage-leas/schools",
          element: <Components.ManageSchoolsPage />,
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: "assignment-master",
          element: <Components.AssignmentMaster />,
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: "reports",
          element: <Components.SuperAdminReport />,
          errorElement: <RouteErrorBoundary />,
        },
      ];

    case "LEA_Admin":
      return [
        {
          path: "dashboard",
          element: <Components.DistrictAdminDashboard />,
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: "manage-admins",
          element: <Components.ManageLEAAdmins />,
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: "manage-schools",
          element: <Components.ManageSchoolsPage />,
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: "assignment-master",
          element: <Components.AssignmentMaster />,
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: "reports",
          element: <Components.DistrictAdminReportPage />,
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: "recovery-requests",
          element: <Components.AttendaceRecoveryRequests />,
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: "all-recovery-requests",
          element: <Components.AllRecoveryRequests />,
          errorElement: <RouteErrorBoundary />,
        },
      ];

    case "School_Admin":
      return [
        {
          path: "dashboard",
          element: <Components.SchoolDashboard />,
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: "reports",
          element: <Components.SchoolReportPage />,
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: "school-users",
          element: <Components.RoastInfoScreen />,
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: "recovery-requests",
          element: <Components.AttendaceRecoveryRequests />,
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: "all-recovery-requests",
          element: <Components.AllRecoveryRequests />,
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: "master/others",
          element: <Components.Others />,
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: "is-management",
          element: <Components.SchoolAdminFlowPage />,
          errorElement: <RouteErrorBoundary />,
        },
      ];

    case "Parent":
      return [
        {
          path: "attendance-recovery/list",
          element: <Components.LeavesPage />,
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: "attendance-recovery/calendar",
          element: <Components.LeavesPage />,
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: "recovery-requests",
          element: <Components.AttendaceRecoveryRequests />,
          errorElement: <RouteErrorBoundary />,
        },
      ];

    case "Teacher":
      return [
        {
          path: "recovery-requests",
          element: <Components.AttendaceRecoveryRequests />,
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: "submitted-assignments",
          element: <Components.AssignmentsPage />,
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: "all-recovery-requests",
          element: <Components.AllRecoveryRequests />,
          errorElement: <RouteErrorBoundary />,
        },
      ];

    case "Student":
      return [
        {
          path: "assignments",
          element: <Components.AssignmentsPage />,
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: "downloaded-meterials",
          element: <Components.Materials />,
          errorElement: <RouteErrorBoundary />,
        },
      ];

    case "AttandanceCoordinator":
      return [
        {
          path: "attendance-recovery-manual-form",
          element: <Components.AttendaceRecoveryManualForm />,
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: "recovery-requests",
          element: <Components.AttendaceRecoveryRequests />,
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: "all-recovery-requests",
          element: <Components.AllRecoveryRequests />,
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: "submitted-assignments",
          element: <Components.AssignmentsPage />,
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: "reports",
          element: <Components.ClerkReportPage />,
          errorElement: <RouteErrorBoundary />,
        },
      ];

    case "Clerk":
      return [
        {
          path: "attendance-recovery-manual-form",
          element: <Components.AttendaceRecoveryManualForm />,
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: "recovery-requests",
          element: <Components.AttendaceRecoveryRequests />,
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: "all-recovery-requests",
          element: <Components.AllRecoveryRequests />,
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: "submitted-assignments",
          element: <Components.AssignmentsPage />,
          errorElement: <RouteErrorBoundary />,
        },
        {
          path: "reports",
          element: <Components.ClerkReportPage />,
          errorElement: <RouteErrorBoundary />,
        },
      ];

    default:
      return [];
  }
}

/**
 * Get the initial path for a user role after authentication
 * @param role - User role string
 * @returns Initial path string or null if invalid role
 */
export function getInitialPath(role: string | null): string | null {
  // If role is invalid, return null to handle redirect to login at router level
  if (!role) {
    return null;
  }

  // Handle Default_Role by returning null (unauthenticated users)
  if (role === "Default_Role") {
    return null;
  }

  // Use the configuration object for cleaner code
  return roleInitialPaths[role] || null;
}

/**
 * Check if a role is valid and has defined routes
 * @param role - User role string
 * @returns Boolean indicating if role is valid
 */
export function isValidRole(role: string | null): boolean {
  if (!role || role === "Default_Role") {
    return false;
  }

  return Object.keys(roleInitialPaths).includes(role);
}

/**
 * Get routes for a user based on their primary role and capabilities.
 * Merges routes from all capable roles, removing duplicates.
 * @param primaryRole - User's primary role
 * @param capabilities - Array of capability types
 * @param advanceCapabilities - Whether user has advance capabilities
 * @returns Array of RouteObject with merged routes
 */
export function getRoutesForUser(
  primaryRole: RoleTypes,
  capabilities: CapabilityType[] = [],
  advanceCapabilities: boolean = false,
): RouteObject[] {
  // Start with primary role routes
  const primaryRoutes = getRoutes(primaryRole);

  // If no advance capabilities, just return primary role routes
  if (!advanceCapabilities || capabilities.length === 0) {
    return primaryRoutes;
  }

  // Collect all routes, tracking by path to avoid duplicates
  const routeMap = new Map<string, RouteObject>();

  // Add primary role routes first
  primaryRoutes.forEach((route) => {
    if (route.path) {
      routeMap.set(route.path, route);
    }
  });

  // Add routes from each capability
  capabilities.forEach((capability) => {
    const role = capabilityToRole[capability];
    if (role && role !== primaryRole) {
      const capabilityRoutes = getRoutes(role);
      capabilityRoutes.forEach((route) => {
        if (route.path && !routeMap.has(route.path)) {
          routeMap.set(route.path, route);
        }
      });
    }
  });

  return Array.from(routeMap.values());
}
